/**
 * Kleine IndexedDB-Hülle für die lokale Kopie (Phase 4).
 * Ist IndexedDB nicht verfügbar (z. B. privates Fenster), arbeitet die App ohne Offline-Puffer.
 */
const DB_NAME = 'kartothek-offline';
const VERSION = 1;

export type StoreName = 'responses' | 'outbox' | 'meta' | 'problems';

let dbPromise: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, VERSION);
    } catch {
      return resolve(null);
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      // GET-Antworten nach URL (Notizen, Karten, Kämpfe, Listen)
      if (!db.objectStoreNames.contains('responses')) db.createObjectStore('responses');
      // Gepufferte Änderungen in Reihenfolge
      if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox', { keyPath: 'seq', autoIncrement: true });
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta');
      if (!db.objectStoreNames.contains('problems')) db.createObjectStore('problems', { keyPath: 'id', autoIncrement: true });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(name: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
  const db = await open();
  if (!db) return undefined;
  try {
    return await wrap(fn(db.transaction(name, mode).objectStore(name)));
  } catch {
    return undefined;
  }
}

export async function idbAvailable(): Promise<boolean> {
  return (await open()) !== null;
}

export function idbGet<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
  return withStore(store, 'readonly', (s) => s.get(key) as IDBRequest<T>);
}

export async function idbPut(store: StoreName, value: unknown, key?: IDBValidKey): Promise<IDBValidKey | undefined> {
  return withStore(store, 'readwrite', (s) => (key === undefined ? s.put(value) : s.put(value, key)));
}

export async function idbDelete(store: StoreName, key: IDBValidKey | IDBKeyRange): Promise<void> {
  await withStore(store, 'readwrite', (s) => s.delete(key));
}

export async function idbGetAll<T>(store: StoreName): Promise<T[]> {
  return (await withStore(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>)) ?? [];
}

export async function idbCount(store: StoreName, range?: IDBKeyRange): Promise<number> {
  return (await withStore(store, 'readonly', (s) => s.count(range))) ?? 0;
}

/** Schlüssel mit gemeinsamem Präfix (z. B. alle URLs einer Kampagne). */
export function prefixRange(prefix: string): IDBKeyRange {
  return IDBKeyRange.bound(prefix, `${prefix}￿`);
}

export async function idbKeys(store: StoreName, range?: IDBKeyRange): Promise<string[]> {
  return ((await withStore(store, 'readonly', (s) => s.getAllKeys(range))) ?? []).map(String);
}

/** Mehrere Schreibvorgänge in einer Transaktion (schnell bei großen Abgleichen). */
export async function idbBatch(store: StoreName, ops: { key: IDBValidKey; value?: unknown; delete?: boolean }[]): Promise<void> {
  const db = await open();
  if (!db || ops.length === 0) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(store, 'readwrite');
    const s = tx.objectStore(store);
    for (const op of ops) {
      if (op.delete) s.delete(op.key);
      else s.put(op.value, op.key);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });
}

export async function idbClear(store: StoreName): Promise<void> {
  await withStore(store, 'readwrite', (s) => s.clear());
}
